/**LAGOSEcoLabels — /state/lagos/eco-labels — Upgraded with charts + search + sector breakdown */
import React, { useEffect, useState, useMemo } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import client from "@/api/client";
import { Award, QrCode, Search, BarChart2 } from "lucide-react";

const LABEL_COLORS={Platinum:{c:"#7c3aed",bg:"#f5f3ff",border:"#ddd6fe"},Gold:{c:"#d97706",bg:"#fffbeb",border:"#fde68a"},Silver:{c:"#64748b",bg:"#f8fafc",border:"#e2e8f0"},Bronze:{c:"#b45309",bg:"#fef3c7",border:"#fde68a"},Emerging:{c:"#94a3b8",bg:"#f8fafc",border:"#e2e8f0"}};
const LABEL_CRITERIA={
  Platinum:"Composite ≥82 + T1/T2 evidence required · Exceptional climate performance + verified circularity",
  Gold:"Composite ≥68 + medium-to-high evidence · Strong performance + adequate evidence quality",
  Silver:"Composite ≥53 · Improving performance + adequate reporting",
  Bronze:"Composite ≥38 · Below average + basic reporting",
  Emerging:"Composite <38 · Baseline only — immediate TA needed"
};
const LABEL_ORDER = ["Platinum","Gold","Silver","Bronze","Emerging"];

export default function LAGOSEcoLabels() {
  const { selectedPeriod, loading:ctxLoading } = useLagos();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    client.get(`/lagos/eco-labels/${selectedPeriod}`).then(r=>setData(r.data)).finally(()=>setLoading(false));
  }, [selectedPeriod]);

  const byLabel = data?.by_label || {};
  const all = data?.entities || [];
  const total = data?.total || all.length;

  // Filtered list
  const baseList = selected === "all" ? all : (byLabel[selected] || []);
  const filtered = useMemo(() => {
    if (!search) return baseList;
    const q = search.toLowerCase();
    return baseList.filter(e =>
      e.name?.toLowerCase().includes(q) ||
      e.lga?.toLowerCase().includes(q) ||
      e.sector?.toLowerCase().includes(q)
    );
  }, [baseList, search]);

  // Sector distribution per label
  const sectorBreakdown = useMemo(() => {
    const m = {};
    all.forEach(e => {
      const lbl = e.eco_label || "Emerging";
      const sec = (e.sector || "unknown").replace(/_/g, " ");
      if (!m[lbl]) m[lbl] = {};
      m[lbl][sec] = (m[lbl][sec] || 0) + 1;
    });
    return m;
  }, [all]);

  if (ctxLoading || loading) return <div className="p-8 text-slate-400 text-sm">Loading eco labels…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* Header */}
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
          Lagos State Eco-Label Register
        </span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">Eco Labels</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          Fully automatic · Derived from SSOT scores · QR-verified · No manual override permitted
        </p>
      </div>

      {/* Methodology note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
        <strong>Strict rule:</strong> Labels are automatically derived from Climate score (40%), Circularity score (30%), and Data Confidence (30%). No manual override permitted. Label changes only through improved inputs + recalculation.
      </div>

      {/* Distribution visual + label selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Label count cards */}
        <div className="grid grid-cols-5 gap-2">
          {LABEL_ORDER.map(lbl => {
            const items = byLabel[lbl] || [];
            const lc = LABEL_COLORS[lbl];
            const pct = total > 0 ? ((items.length / total) * 100).toFixed(0) : 0;
            return (
              <button key={lbl} onClick={() => setSelected(s => s === lbl ? "all" : lbl)}
                className={`rounded-xl p-3 text-center border-2 transition-all ${selected === lbl ? "shadow-md" : "opacity-80 hover:opacity-100"}`}
                style={{ background: lc.bg, borderColor: selected === lbl ? lc.c : lc.border }}
                data-testid={`label-filter-${lbl}`}>
                <Award size={16} className="mx-auto mb-1.5" style={{ color: lc.c }} />
                <p className="text-xs font-black" style={{ color: lc.c }}>{lbl}</p>
                <p className="text-xl font-black text-slate-900 mt-0.5">{items.length}</p>
                <p className="text-[9px] text-slate-400">{pct}%</p>
              </button>
            );
          })}
        </div>

        {/* Distribution bar chart */}
        <SectionCard title="Portfolio Label Distribution">
          <div className="p-4 space-y-2.5">
            {LABEL_ORDER.map(lbl => {
              const count = (byLabel[lbl] || []).length;
              const pct = total > 0 ? (count / total) * 100 : 0;
              const lc = LABEL_COLORS[lbl];
              return (
                <div key={lbl} className="space-y-0.5">
                  <div className="flex justify-between text-xs">
                    <span className="font-bold" style={{ color: lc.c }}>{lbl}</span>
                    <span className="text-slate-500">{count} entities · {pct.toFixed(0)}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: pct + "%", background: lc.c }} />
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Sector breakdown for selected label */}
      {selected !== "all" && sectorBreakdown[selected] && (
        <SectionCard title={`${selected} — Sector Breakdown`}>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(sectorBreakdown[selected])
                .sort((a, b) => b[1] - a[1])
                .map(([sec, count]) => {
                  const lc = LABEL_COLORS[selected];
                  return (
                    <div key={sec} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs"
                      style={{ background: lc.bg, borderColor: lc.border }}>
                      <span className="font-medium capitalize">{sec}</span>
                      <span className="font-black" style={{ color: lc.c }}>{count}</span>
                    </div>
                  );
                })
              }
            </div>
          </div>
        </SectionCard>
      )}

      {/* Criteria Reference */}
      <SectionCard title="Label Criteria — Composite = Climate (40%) + Circularity (30%) + Confidence (30%)">
        <div className="p-4 space-y-1">
          {LABEL_ORDER.map(l => (
            <div key={l} className="flex gap-3 text-xs py-2 border-b border-slate-50 last:border-0">
              <span className="font-bold w-16 flex-shrink-0" style={{ color: LABEL_COLORS[l].c }}>{l}</span>
              <span className="text-slate-500">{LABEL_CRITERIA[l]}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Entity Table with search */}
      <SectionCard title={`${selected === "all" ? "All" : selected} Labels (${filtered.length})`}>
        <div className="p-4">
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search entity, LGA, sector…"
                className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                data-testid="eco-label-search"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Entity","LGA","Sector","Climate","Circularity","Confidence","Label","Valid Until","Verify"].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 50).map(e => {
                  const lc = LABEL_COLORS[e.eco_label || "Emerging"];
                  return (
                    <tr key={e.entity_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-3 font-medium max-w-[140px] truncate">{e.name}</td>
                      <td className="py-2.5 px-3 text-slate-500">{e.lga}</td>
                      <td className="py-2.5 px-3 text-slate-500 capitalize">{(e.sector || "").replace(/_/g, " ")}</td>
                      <td className="py-2.5 px-3 font-bold">{(e.climate_score || 0).toFixed(0)}</td>
                      <td className="py-2.5 px-3 font-bold">{(e.circularity_score || 0).toFixed(0)}</td>
                      <td className="py-2.5 px-3 font-bold">{(e.data_confidence_score || 0).toFixed(0)}</td>
                      <td className="py-2.5 px-3">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                          style={{ background: lc.bg, color: lc.c, border: `1px solid ${lc.border}` }}>
                          {e.eco_label}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 text-[10px]">{e.valid_until || "—"}</td>
                      <td className="py-2.5 px-3">
                        {e.label_hash && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/lagos/verify-label/${e.label_hash}`}
                            target="_blank" rel="noreferrer"
                            className="text-blue-500 flex items-center gap-1 text-[10px] hover:text-blue-700">
                            <QrCode size={10} /> Verify
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length > 50 && (
            <p className="text-[10px] text-slate-400 text-center pt-3">Showing 50 of {filtered.length} · Use search to filter.</p>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
